import { Capacitor } from '@capacitor/core';

export interface LibraryAsset {
  id: string;
  thumb: string;
  isVideo: boolean;
  duration?: number;
}

// The camera-roll plugin can only list assets on iOS. Android and web fall back
// to the system photo picker (multi-select file input).
export const canListLibrary = () => Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';

const MIME_BY_EXT: Record<string, string> = {
  jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', heic: 'image/heic', heif: 'image/heif', gif: 'image/gif', webp: 'image/webp',
  mp4: 'video/mp4', mov: 'video/quicktime', m4v: 'video/mp4', webm: 'video/webm',
};

export async function listRecents(quantity = 120): Promise<LibraryAsset[]> {
  const { Media } = await import('@capacitor-community/media');
  const res = await Media.getMedias({
    quantity,
    types: 'all',
    thumbnailWidth: 360,
    thumbnailHeight: 360,
    thumbnailQuality: 70,
    sort: [{ key: 'creationDate', ascending: false }],
  });
  return res.medias.map((m) => ({
    id: m.identifier,
    thumb: m.data.startsWith('data:') ? m.data : `data:image/jpeg;base64,${m.data}`,
    isVideo: typeof m.duration === 'number' && m.duration > 0,
    duration: m.duration,
  }));
}

export async function resolveAsset(asset: LibraryAsset): Promise<File> {
  const { Media } = await import('@capacitor-community/media');
  const { path } = await Media.getMediaByIdentifier({ identifier: asset.id });
  const blob = await (await fetch(Capacitor.convertFileSrc(path))).blob();
  const ext = (path.split('.').pop() || '').toLowerCase();
  const type = blob.type && blob.type !== 'application/octet-stream' ? blob.type : (MIME_BY_EXT[ext] || (asset.isVideo ? 'video/mp4' : 'image/jpeg'));
  const name = path.split('/').pop() || `media.${ext || (asset.isVideo ? 'mp4' : 'jpg')}`;
  return new File([blob], name, { type });
}
